import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import type { CatalogCollection, CatalogColor, CatalogWindow } from "../lib/api/types";
import type { DoorSelection, SectionKey } from "../lib/state/visualizerStore";
import { dollars } from "../lib/format";
import { ManufacturerSelect } from "./ManufacturerSelect";
import { Thumb } from "./Thumb";
import {
  buildTwoToneModel,
  colorNameKey,
  hasTwoToneColors,
  isComboAvailable,
  isTwoToneCombo,
  resolveTwoTone,
  selectedTwoToneParts,
  splitTwoToneName,
  type TwoToneModel,
} from "../lib/twoTone";

interface Props {
  collection: CatalogCollection;
  collections: CatalogCollection[];
  selection: DoorSelection;
  open: SectionKey; // controlled accordion — which section is expanded
  // Size(s) of the door(s) being styled (8 single / 16 double). Individual mode passes one width;
  // ALL-doors mode passes every distinct door width — an option must be renderable at ALL of them
  // (a double-only panel offered to a single+double pair would blank the single door).
  widthsFt: number[];
  // Priced (dealer) mode shows the insulation "+$X" upcharge; showroom (price-free) hides it.
  showPrices: boolean;
  onToggleSection: (key: SectionKey) => void;
  onPickCollection: (c: CatalogCollection) => void;
  onChange: (patch: Partial<DoorSelection>) => void;
}

export function DoorPicker({ collection, collections, selection, open, widthsFt, showPrices, onToggleSection, onPickCollection, onChange }: Props) {
  // Picking an option NEVER auto-advances the accordion — the customer keeps browsing
  // the current section until they choose to move on.
  const toggle = (k: SectionKey) => onToggleSection(open === k ? "" : k);

  // Hide options that have no render at EVERY styled door size (8×7 single / 16×7 double). An empty/absent
  // availability list means "every size" (back-compat with catalogues that predate per-size availability).
  const fitsSize = (widths?: number[]) => !widths || widths.length === 0 || widthsFt.every((w) => widths.includes(w));

  const designs = collection.designs.filter((d) => fitsSize(d.availableWidthsFt));
  // Colours are scoped to the SELECTED design's JOINT availability: manufacturers offer colours per design
  // (Clopay's paint finishes only on mahogany-base panels, Amarr's Flush panel only in White). Offering a
  // colour the chosen design never renders would resolve to no asset and blank the door — so hide it. A
  // design with no availableColorIds (pre-feature data) imposes no restriction.
  const activeDesign = collection.designs.find((d) => d.id === selection.designId);
  const colorFitsDesign = (c: CatalogColor) =>
    !activeDesign?.availableColorIds?.length || activeDesign.availableColorIds.includes(c.id);
  const colors = collection.colors.filter((c) => fitsSize(c.availableWidthsFt) && colorFitsDesign(c));
  const hardware = collection.hardware.filter((h) => fitsSize(h.availableWidthsFt));

  // A full-view / all-glass line (no closed product) must not offer a "None"/closed layout — its door is
  // always glazed. Absent flag ⇒ assume closed IS supported (back-compat with pre-feature API responses).
  const supportsClosed = collection.supportsClosed ?? true;

  // Switching design may invalidate the current colour (the new design might not render it). Keep it when
  // still available, else fall back to the new design's first size-eligible colour, so the selection never
  // points at a colour the design can't render.
  const colorForDesign = (designId: string): string | null => {
    const nd = collection.designs.find((d) => d.id === designId);
    const allowed = nd?.availableColorIds ?? [];
    if (allowed.length === 0) return selection.colorId; // no restriction — keep current
    if (selection.colorId && allowed.includes(selection.colorId)) return selection.colorId;
    return collection.colors.find((c) => allowed.includes(c.id) && fitsSize(c.availableWidthsFt))?.id ?? selection.colorId;
  };

  // Manufacturer axis (multi-brand catalogues — the all-manufacturers demo, a multi-manufacturer
  // dealer). It's its OWN section ABOVE Collection: pick a manufacturer → the Collection section lists
  // only that manufacturer's collections. Single-brand catalogues skip it entirely.
  const manufacturers: { id: string; name: string; count: number }[] = [];
  for (const c of collections) {
    const id = c.manufacturer?.id ?? "";
    const existing = manufacturers.find((m) => m.id === id);
    if (existing) existing.count++;
    else manufacturers.push({ id, name: c.manufacturer?.name ?? "Other", count: 1 });
  }
  const multiBrand = manufacturers.length > 1;
  // Active manufacturer follows the selected collection's brand, but the customer can switch it to
  // browse another manufacturer's collections. Re-sync when the selected collection's brand changes
  // (e.g. switching between doors) — a click on a manufacturer tile doesn't change `collection`, so it
  // isn't clobbered by this effect.
  const [manufId, setManufId] = useState(collection.manufacturer?.id ?? "");
  useEffect(() => { setManufId(collection.manufacturer?.id ?? ""); }, [collection.manufacturer?.id]);
  const brandCollections = multiBrand ? collections.filter((c) => (c.manufacturer?.id ?? "") === manufId) : collections;
  const activeManufName = manufacturers.find((m) => m.id === manufId)?.name;

  const design = collection.designs.find((d) => d.id === selection.designId);
  // Show the door's window / top-section options, but drop a generic no-glass "Closed" entry — the
  // "None" tile already represents the plain closed door (and under binary pricing it would wrongly read
  // as windowed). Named no-glass top sections (e.g. Carriage Court "Closed Square"/"Closed Arch") are
  // kept — they're distinct, separately-priced choices.
  const windows = (design
    ? collection.windows.filter((w) => design.availableWindowOptionIds.includes(w.id))
    : collection.windows
  ).filter((w) => w.windowDesign.trim().toLowerCase() !== "closed").filter((w) => fitsSize(w.availableWidthsFt));

  // --- Windows section: LAYOUT (placement) → STYLE (insert design) → GLASS (tint) --------------------
  // Placements are matched case-insensitively — manufacturers disagree on casing ("Second Row" vs
  // "SECOND ROW"). All-caps labels are title-cased for display only; matching always uses the norm key.
  const normPlacement = (p: string) => p.trim().toLowerCase();
  const prettyPlacement = (p: string) => {
    const t = p.trim();
    return /[A-Z]/.test(t) && t === t.toUpperCase() ? t.toLowerCase().replace(/(^|\s)\S/g, (m) => m.toUpperCase()) : t;
  };

  // Distinct layouts (placements) in first-appearance order (the catalogue's SortOrder puts Top first).
  const layouts: { key: string; label: string }[] = [];
  for (const w of windows) {
    const key = normPlacement(w.placement);
    if (!layouts.some((l) => l.key === key)) layouts.push({ key, label: prettyPlacement(w.placement) });
  }
  const multiLayout = layouts.length > 1;
  // A "Closed" layout (named closed top sections — Closed Square/Arch) IS the no-windows choice, and
  // its render matches what a null selection falls back to — a separate "None" tile would duplicate
  // it. Collections without one keep the synthetic None.
  const hasClosedLayout = layouts.some((l) => l.key === "closed");

  const selectedColor = collection.colors.find((c) => c.id === selection.colorId);

  // --- Color section: two-tone Base + Overlay sub-picker (Raynor RockCreeke-style) -----------------
  // Those collections carry one pre-combined colour row per base × face (7 bases × 9 faces = 63
  // swatches flat) — split them into two short rows instead. Detection is data-driven (family
  // "Two-Tone" + "Base / Face" names), so Amarr/CHI/single-colour collections keep the flat grid.
  // Picking base + face resolves back to ONE pre-combined row id — pricing/rendering are untouched.
  const twoToneModel = hasTwoToneColors(colors) ? buildTwoToneModel(colors) : null;
  const selectedCombo = twoToneModel && selectedColor && isTwoToneCombo(selectedColor) ? splitTwoToneName(selectedColor.name) : null;
  const selectedWindow = selection.windowOptionId
    ? collection.windows.find((w) => w.id === selection.windowOptionId)
    : undefined;
  const selectedHardware = selection.hardwareId
    ? collection.hardware.find((h) => h.id === selection.hardwareId)
    : undefined;
  // Insulation (non-visual): the dealer's chosen construction model. Sessions persisted before the feature
  // rehydrate without constructionId — read it defensively. Only present when the dealer enabled insulation
  // (the server sends an empty list otherwise, so the section never renders).
  const selectedConstruction = selection.constructionId
    ? collection.constructions.find((c) => c.id === selection.constructionId)
    : undefined;

  // The current layout is DERIVED from the selected window's placement (null = "None") — no extra state.
  const layoutKey = selectedWindow ? normPlacement(selectedWindow.placement) : null;
  // Styles offered in the Windows section: the chosen layout's inserts; a single-layout catalogue
  // (Classica, Carriage Court, full-view lines…) has no Layout section, so every window is a style.
  const styleChoices = multiLayout
    ? layoutKey !== null
      ? windows.filter((w) => normPlacement(w.placement) === layoutKey)
      : []
    : windows;

  // Icon consistency: the same insert name can carry wildly different art per placement (full-door render
  // for one, uniform strip icon for another). Prefer the windowDesign's "Top"-placement icon when the
  // collection has one — Amarr's Top icons are a uniform strip set — else the option's own image.
  const styleIcon = (w: CatalogWindow): string | null =>
    collection.windows.find(
      (x) =>
        normPlacement(x.placement) === "top" &&
        x.windowDesign.trim().toLowerCase() === w.windowDesign.trim().toLowerCase() &&
        !!x.imageUrl,
    )?.imageUrl ?? w.imageUrl;

  // Glass tints available for the selected window, in bundle order. Sessions persisted before glassTypeId
  // rehydrate without the field — read it defensively as null.
  const selectedGlassId = selection.glassTypeId ?? null;
  const availableGlassIds = selectedWindow?.availableGlassTypeIds ?? [];
  const glassOptions = availableGlassIds.length > 0 ? collection.glassTypes.filter((g) => availableGlassIds.includes(g.id)) : [];
  const selectedGlass = selectedGlassId ? collection.glassTypes.find((g) => g.id === selectedGlassId) : undefined;

  // Tint to apply when a window is picked: keep the current tint if the new window also offers it, else
  // its first tint — never null while tints exist, so the render matches what the Glass row shows.
  const glassIdFor = (w: CatalogWindow | undefined): string | null => {
    const avail = w?.availableGlassTypeIds ?? [];
    if (!w || avail.length === 0) return null;
    if (selectedGlassId && avail.includes(selectedGlassId)) return selectedGlassId;
    return collection.glassTypes.find((g) => avail.includes(g.id))?.id ?? avail[0];
  };

  // Picking a layout immediately selects its first style so the door preview updates; None clears both
  // the window and the tint. Re-clicking the current layout is a no-op (keeps the chosen style).
  const pickLayout = (key: string | null) => {
    if (key === layoutKey) return;
    if (key === null) {
      onChange({ windowOptionId: null, glassTypeId: null });
      return;
    }
    const first = windows.find((w) => normPlacement(w.placement) === key);
    if (first) onChange({ windowOptionId: first.id, glassTypeId: glassIdFor(first) });
  };

  const pickStyle = (id: string) => {
    const w = collection.windows.find((x) => x.id === id);
    onChange({ windowOptionId: id, glassTypeId: glassIdFor(w) });
  };

  const designName = design?.name;
  const colorName = selectedColor?.name;
  const hardwareName = selectedHardware?.name;
  const constructionName = selectedConstruction?.name;

  // No summary chips row here — the collapsed section headers 10px below already show the same
  // values (and the recap survives in the DoorLegend row and the price-card title).
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {multiBrand && (
        <Section title="Manufacturer" value={activeManufName} isOpen={open === "manufacturer"} onToggle={() => toggle("manufacturer")}>
          {/* Selecting a manufacturer reveals its collections: switch the brand and open Collection. */}
          <ManufacturerSelect
            options={manufacturers}
            selectedId={manufId}
            onSelect={(id) => { setManufId(id); onToggleSection("collection"); }}
            testId="manufacturer-tiles"
          />
        </Section>
      )}

      {collections.length > 1 && (
        <Section title="Collection" value={collection.name} preview={collection.imageUrl} previewFit="contain" previewAspect="16 / 5" isOpen={open === "collection"} onToggle={() => toggle("collection")}>
          <Tiles
            items={brandCollections.map((c) => ({ id: c.id, name: c.name, imageUrl: c.imageUrl }))}
            selectedId={collection.id}
            onPick={(id) => { const c = collections.find((x) => x.id === id); if (c) onPickCollection(c); }}
          />
        </Section>
      )}

      <Section title="Panel" value={designName} preview={design?.imageUrl} previewFit="contain" previewAspect="16 / 7" isOpen={open === "design"} onToggle={() => toggle("design")}>
        {designs.length > 0 ? (
          <Tiles
            items={designs.map((d) => ({ id: d.id, name: d.name, imageUrl: d.imageUrl }))}
            selectedId={selection.designId}
            cols={2}
            fit="contain"
            onPick={(id) => onChange({ designId: id, colorId: colorForDesign(id), windowOptionId: null, glassTypeId: null })}
          />
        ) : (
          <div style={{ fontSize: 13, color: "var(--muted)", padding: "4px 0" }}>
            {widthsFt.length === 1
              ? `No ${widthsFt[0] === 8 ? "single" : "double"}-car styles available for this collection.`
              : "No styles available for all of your door sizes in this collection."}
          </div>
        )}
      </Section>

      <Section
        title="Color"
        value={colorName}
        preview={selectedCombo ? null : selectedColor?.imageUrl}
        // A combined selection summarizes as a split base/face swatch — its halves reuse the row art.
        previewNode={selectedCombo ? (
          <SplitSwatch
            baseUrl={twoToneModel!.bases.find((b) => b.key === colorNameKey(selectedCombo.base))?.imageUrl ?? selectedColor?.imageUrl ?? null}
            faceUrl={twoToneModel!.faces.find((f) => f.key === colorNameKey(selectedCombo.face))?.imageUrl ?? selectedColor?.imageUrl ?? null}
          />
        ) : undefined}
        previewKind="swatch"
        isOpen={open === "color"}
        onToggle={() => toggle("color")}
      >
        {twoToneModel ? (
          <TwoToneColorTiles colors={colors} model={twoToneModel} selected={selectedColor} onPick={(id) => onChange({ colorId: id })} />
        ) : (
          <Tiles
            items={colors.map((c) => ({ id: c.id, name: c.name, imageUrl: c.imageUrl }))}
            selectedId={selection.colorId}
            minTile={76}
            imgAspect="1 / 1"
            onPick={(id) => onChange({ colorId: id })}
          />
        )}
      </Section>

      {/* Window choices appear only when applicable — Layout when the collection offers competing
          placements, Windows (insert styles) whenever windowed options exist for this door, Glass when
          the chosen window has a real tint choice. Single-layout catalogues (Classica, Carriage Court,
          full-view lines…) skip straight to the classic None + styles list. */}
      {windows.length > 0 && multiLayout && (
        <Section
          title="Layout"
          value={layoutKey === null ? (hasClosedLayout ? undefined : "None") : layouts.find((l) => l.key === layoutKey)?.label}
          previewNode={<LayoutGlyph placement={layoutKey} width={40} />}
          isOpen={open === "layout"}
          onToggle={() => toggle("layout")}
        >
          {/* Equal-column grid: flex-wrap sized tiles to their label text, so "Top" and
              "Vertical Both Sides" came out different widths and the rows never lined up. */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))", gap: 8 }}>
            {!hasClosedLayout && supportsClosed && (
              <LayoutTile label="None" placement={null} selected={layoutKey === null} onClick={() => pickLayout(null)} />
            )}
            {layouts.map((l) => (
              <LayoutTile key={l.key} label={l.label} placement={l.key} selected={layoutKey === l.key} onClick={() => pickLayout(l.key)} />
            ))}
          </div>
        </Section>
      )}

      {windows.length > 0 && (
        <Section
          title="Windows"
          value={selectedWindow?.name ?? "None"}
          preview={selectedWindow ? styleIcon(selectedWindow) : null}
          previewFit="contain"
          previewAspect="16 / 5"
          isOpen={open === "window"}
          onToggle={() => toggle("window")}
        >
          {multiLayout && layoutKey === null ? (
            <div style={{ fontSize: 13, color: "var(--muted)", padding: "4px 0" }}>
              No windows — pick a layout above to add them.
            </div>
          ) : (
            <Tiles
              items={[
                // With a Layout section, "None" lives there; single-layout catalogues keep it here — but a
                // full-view / all-glass line (no closed product) never offers it.
                ...(!multiLayout && supportsClosed ? [{ id: "", name: "None", imageUrl: null }] : []),
                ...styleChoices.map((w) => ({ id: w.id, name: w.name, imageUrl: styleIcon(w) })),
              ]}
              selectedId={selection.windowOptionId ?? ""}
              cols={2}
              fit="contain"
              imgAspect="16 / 5"
              onPick={(id) => (id ? pickStyle(id) : onChange({ windowOptionId: null, glassTypeId: null }))}
            />
          )}
        </Section>
      )}

      {glassOptions.length > 1 && (
        <Section
          title="Glass"
          value={selectedGlass?.name}
          preview={selectedGlass?.imageUrl}
          previewKind="swatch"
          isOpen={open === "glass"}
          onToggle={() => toggle("glass")}
        >
          <Tiles
            items={glassOptions.map((g) => ({ id: g.id, name: g.name, imageUrl: g.imageUrl }))}
            selectedId={selectedGlassId ?? ""}
            minTile={76}
            imgAspect="1 / 1"
            onPick={(id) => onChange({ glassTypeId: id })}
          />
        </Section>
      )}

      {hardware.length > 0 && (
        <Section title="Hardware" value={hardwareName ?? "None"} preview={selectedHardware?.imageUrl} previewFit="contain" isOpen={open === "hardware"} onToggle={() => toggle("hardware")}>
          <Tiles
            items={[{ id: "", name: "None", imageUrl: null }, ...hardware.map((h) => ({ id: h.id, name: h.name, imageUrl: h.imageUrl }))]}
            selectedId={selection.hardwareId ?? ""}
            onPick={(id) => onChange({ hardwareId: id || null })}
          />
        </Section>
      )}

      {/* Insulation (non-visual): the collection's construction models, shown only when the dealer enabled
          insulation (server sends an empty list otherwise). Priced mode shows each model's "+$X"; showroom
          shows the choice with no price. A model may carry a small image, else the tile is text-only. */}
      {collection.constructions.length > 0 && (
        <Section title="Insulation" value={constructionName ?? "None"} preview={selectedConstruction?.imageUrl} previewFit="contain" isOpen={open === "insulation"} onToggle={() => toggle("insulation")}>
          <Tiles
            items={[
              { id: "", name: "None", imageUrl: null },
              ...collection.constructions.map((c) => ({
                id: c.id,
                name: c.name,
                imageUrl: c.imageUrl,
                note: showPrices && c.upchargeCents > 0 ? `+${dollars(c.upchargeCents)}` : undefined,
              })),
            ]}
            selectedId={selection.constructionId ?? ""}
            onPick={(id) => onChange({ constructionId: id || null })}
          />
        </Section>
      )}
    </div>
  );
}

function Section({ title, value, preview, previewNode, isOpen, onToggle, children, previewFit = "cover", previewAspect = "16 / 6", previewKind = "band" }: {
  title: string;
  value?: string;
  preview?: string | null;
  previewNode?: ReactNode; // non-image inline preview (e.g. the Layout schematic glyph)
  isOpen: boolean;
  onToggle: () => void;
  children: ReactNode;
  previewFit?: "cover" | "contain"; // "contain" frames option art uncropped on --bg (panels/windows)
  previewAspect?: string; // band aspect ratio per section type
  previewKind?: "band" | "swatch"; // Color uses an inline swatch instead of a wide band
}) {
  // A collapsed section doubles as a visual summary: the wide preview band (or, for Color, an inline
  // swatch) makes the current choice obvious without opening it. Open sections hide the band — the
  // highlighted tile grid below already shows the selection. A broken preview image (e.g. an
  // unmaterialised collection icon) collapses the band cleanly instead of showing a broken glyph.
  // On mobile the band is swapped (via CSS, both stay mounted) for a compact inline thumb in the
  // header row, so every collapsed header is a uniform ~46px and toggling sections doesn't jump the
  // page by a band's height.
  const [bandBroken, setBandBroken] = useState(false);
  useEffect(() => { setBandBroken(false); }, [preview]);
  const showBand = !isOpen && previewKind === "band" && !!preview && !bandBroken;

  const id = useId();
  const headerId = `${id}-h`;
  const bodyId = `${id}-b`;

  // Mobile: bring a newly OPENED section to a fixed anchor just under the sticky preview. Without
  // this, tapping a lower header collapses the open section above it and the tapped section's body
  // lands below the fold — the customer taps "Color" and sees nothing happen. The wasOpen ref
  // guards the closed→open transition only (no scroll on mount/rehydrate). Measuring synchronously
  // is safe: the sibling's collapse commits in the same render, so this effect sees final layout
  // (no rAF — frame callbacks are throttled/suspended in embedded webviews and background tabs).
  // The preview's LIVE rect.bottom (not a constant) tracks the variable-height sticky card.
  const rootRef = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(isOpen);
  useEffect(() => {
    const was = wasOpen.current;
    wasOpen.current = isOpen;
    if (!isOpen || was) return;
    const el = rootRef.current;
    if (!el || typeof window.matchMedia !== "function" || !window.matchMedia("(max-width: 860px)").matches) return;
    const preview = document.querySelector(".rg-visualize-preview");
    if (!preview) return;
    // The preview occludes the anchor target only when it's pinned AND actually sits over this
    // section's column (portrait: same column → yes). In the landscape two-pane layout the sticky
    // preview lives in its OWN column beside the sections — anchoring under its bottom there would
    // scroll the tapped section to mid-screen or below, so the anchor is the viewport top.
    const occludeOf = (prev: Element, section: Element): number => {
      if (getComputedStyle(prev).position !== "sticky") return 0;
      const pr = prev.getBoundingClientRect();
      const er = section.getBoundingClientRect();
      const overlapsX = pr.right > er.left + 8 && pr.left < er.right - 8;
      return overlapsX ? Math.max(0, pr.bottom) : 0;
    };
    const delta = el.getBoundingClientRect().top - occludeOf(preview, el) - 8;
    if (Math.abs(delta) <= 4) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollBy({ top: delta, behavior: reduce ? "auto" : "smooth" });
    // The pinned card can change height DURING the scroll (scroll-linked collapse: its photo cap
    // is a function of scroll position), which moves the anchor we aimed at. One settle pass after
    // the smooth scroll lands corrects any remaining drift instantly.
    const settle = window.setTimeout(() => {
      const el2 = rootRef.current;
      const prev2 = document.querySelector(".rg-visualize-preview");
      if (!el2 || !prev2) return;
      const d2 = el2.getBoundingClientRect().top - occludeOf(prev2, el2) - 8;
      if (Math.abs(d2) > 12) window.scrollBy({ top: d2, behavior: "auto" });
    }, reduce ? 80 : 620);
    return () => window.clearTimeout(settle);
  }, [isOpen]);

  return (
    <div
      ref={rootRef}
      style={{
        border: `1px solid ${isOpen ? "var(--brand)" : "var(--border)"}`,
        borderRadius: "var(--radius)",
        overflow: "hidden",
        background: "var(--surface)",
        boxShadow: isOpen ? "var(--shadow-sm)" : "none",
        transition: "border-color 140ms ease, box-shadow 140ms ease",
      }}
    >
      <button
        type="button"
        id={headerId}
        className="rg-section-head"
        aria-expanded={isOpen}
        aria-controls={bodyId}
        onClick={onToggle}
        style={{ display: "flex", flexDirection: "column", gap: 0, width: "100%", padding: "12px 14px", background: "none", border: "none", cursor: "pointer", color: "var(--text)", textAlign: "left" }}
      >
        {/* Row 1 — label + current value + chevron */}
        <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 8 }}>
          <span style={{ display: "flex", gap: 8, alignItems: "center", minWidth: 0 }}>
            <strong style={{ fontSize: 14, fontWeight: 600 }}>{title}</strong>
            {previewKind === "swatch" && preview && (
              <img src={preview} alt="" style={{ width: 28, height: 28, borderRadius: "var(--radius-sm)", objectFit: "cover", border: "1px solid var(--border-strong)", display: "block" }} />
            )}
            {previewNode && <span style={{ display: "inline-flex", alignItems: "center" }}>{previewNode}</span>}
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {showBand && (
              <img
                src={preview!}
                alt=""
                className="rg-acc-thumb"
                onError={() => setBandBroken(true)}
                style={{ width: 52, height: 30, objectFit: previewFit, borderRadius: 6, border: "1px solid var(--border)", background: "var(--bg)", flexShrink: 0 }}
              />
            )}
            {value && <span style={{ fontSize: 13, fontWeight: 500, color: "var(--muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 140 }}>{value}</span>}
            <Chevron open={isOpen} />
          </span>
        </span>
        {/* Row 2 — wide preview band (collapsed only; desktop only — mobile uses the inline thumb) */}
        {showBand && (
          <span className="rg-acc-band" style={{ marginTop: 8, borderRadius: "var(--radius-sm)", overflow: "hidden", border: "1px solid var(--border)", background: previewFit === "contain" ? "var(--bg)" : "transparent" }}>
            <img src={preview!} alt="" data-testid="preview-band" onError={() => setBandBroken(true)} style={{ width: "100%", aspectRatio: previewAspect, maxHeight: 120, objectFit: previewFit, display: "block", margin: "0 auto" }} />
          </span>
        )}
      </button>
      {isOpen && (
        <div id={bodyId} role="region" aria-labelledby={headerId} className="rg-section-body">
          {children}
        </div>
      )}
    </div>
  );
}

// Schematic glyph for a window layout: a rounded door outline holding a 4×4 grid of panel cells, with the
// cells that carry glass filled in. Strokes use currentColor/--muted so it reads in both themes.
// `placement` is the case-normalized (lowercase) placement key, or null for the "None" layout.
function LayoutGlyph({ placement, width = 52 }: { placement: string | null; width?: number }) {
  const lit = (r: number, c: number): boolean => {
    switch (placement) {
      case null:
      case "closed": // decorative closed top sections (e.g. Carriage Court) — no glazing to highlight
        return false;
      // Top-row glazing across manufacturer vocabularies: Amarr "Top", CHI "FIRST ROW" / Sterling
      // "TOP ROW" / carriage "SINGLE ROW".
      case "top":
      case "first row":
      case "top row":
      case "single row":
        return r === 0;
      case "second row":
        return r === 1;
      case "top 2 rows":
      case "double row":
        return r <= 1;
      case "vertical left":
        return c === 0;
      case "vertical right":
        return c === 3;
      case "vertical both sides":
        return c === 0 || c === 3;
      // CHI Planks/Skyline edge lites and the all-glass layouts (Skyline "ALL WINDOWS", Sterling
      // "ALL GLASS", Aluminum "FULL-VIEW").
      case "left edge":
        return c === 0;
      case "right edge":
        return c === 3;
      case "all windows":
      case "all glass":
      case "full-view":
        return true;
      case "solid bottom row":
        return r < 3;
      case "solid bottom 2 rows":
        return r < 2;
      default:
        // Unrecognized placement — a generic centered 2×2 block signals "windows somewhere on the door".
        return (r === 1 || r === 2) && (c === 1 || c === 2);
    }
  };
  const cells: ReactNode[] = [];
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 4; c++) {
      const on = lit(r, c);
      cells.push(
        <rect
          key={`${r}-${c}`}
          x={5 + c * 11}
          y={5 + r * 7.5}
          width={9}
          height={5.5}
          rx={1}
          fill={on ? "var(--brand-active)" : "none"}
          stroke={on ? "var(--brand-active)" : "var(--muted)"}
          strokeWidth={on ? 0 : 0.75}
          opacity={on ? 1 : 0.55}
        />,
      );
    }
  }
  return (
    <svg viewBox="0 0 52 38" width={width} height={Math.round((width * 38) / 52)} aria-hidden style={{ display: "block", color: "var(--muted)" }}>
      <rect x={1} y={1} width={50} height={36} rx={4} fill="none" stroke="currentColor" strokeWidth={1.25} />
      {cells}
    </svg>
  );
}

// Compact layout tile (glyph + label) — selection styling mirrors the Tiles component's ring.
// Uniform footprint: the tile fills its grid cell and the label gets a fixed two-line block, so
// every tile is the same size regardless of label length ("Top" vs "Vertical Both Sides").
function LayoutTile({ label, placement, selected, onClick }: { label: string; placement: string | null; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      className="rg-tile"
      onClick={onClick}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 6,
        padding: "10px 6px 8px",
        width: "100%",
        background: selected ? "var(--brand-tint)" : "var(--surface)",
        border: `${selected ? "1.5px" : "1px"} solid ${selected ? "var(--brand)" : "var(--border)"}`,
        boxShadow: selected ? "0 0 0 3px var(--ring)" : "none",
        borderRadius: "var(--radius-sm)",
        cursor: "pointer",
      }}
    >
      <LayoutGlyph placement={placement} />
      <span
        style={{
          fontSize: 11,
          lineHeight: "13px",
          height: 26, // two lines, always reserved — keeps every tile the same height
          display: "flex",
          alignItems: "center",
          textAlign: "center",
          fontWeight: selected ? 600 : 400,
          color: "var(--text)",
        }}
      >
        {label}
      </span>
    </button>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      style={{ color: "var(--muted-2)", transform: open ? "rotate(90deg)" : "none", transition: "transform 140ms ease" }}
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

// Two sub-rows replacing the flat colour grid for two-tone collections: Base color (the door's
// main finish) and Overlay color (the accent/face). Picking a base keeps the current overlay when
// that pairing exists — mirroring the glass-tint keep-else-first rule — otherwise it falls to the
// base's first available overlay, so the selection always lands on a real pre-combined row.
// Overlays with no pre-combined row for the chosen base are shown disabled, never fabricated.
function TwoToneColorTiles({ colors, model, selected, onPick }: {
  colors: CatalogColor[];
  model: TwoToneModel;
  selected: CatalogColor | undefined;
  onPick: (id: string) => void;
}) {
  const parts = selectedTwoToneParts(selected);
  const baseKey = parts ? colorNameKey(parts.base) : null;
  const faceKey = parts ? colorNameKey(parts.face) : null;

  const pickBase = (name: string) => {
    const face = parts && isComboAvailable(colors, name, parts.face)
      ? parts.face
      : model.faces.find((f) => isComboAvailable(colors, name, f.name))?.name;
    const resolved = face ? resolveTwoTone(colors, name, face) : undefined;
    if (resolved) onPick(resolved.id);
  };

  const pickFace = (name: string) => {
    if (!parts) return;
    const resolved = resolveTwoTone(colors, parts.base, name);
    if (resolved) onPick(resolved.id);
  };

  const label = (text: string) => (
    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--muted)", marginBottom: 6 }}>{text}</div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div>
        {label("Base color")}
        <Tiles
          items={model.bases.map((b) => ({ id: b.key, name: b.name, imageUrl: b.imageUrl }))}
          selectedId={baseKey}
          minTile={76}
          imgAspect="1 / 1"
          onPick={(key) => { const b = model.bases.find((x) => x.key === key); if (b) pickBase(b.name); }}
        />
      </div>
      <div>
        {label("Overlay color")}
        <Tiles
          items={model.faces.map((f) => ({
            id: f.key,
            name: f.name,
            imageUrl: f.imageUrl,
            disabled: !parts || !isComboAvailable(colors, parts.base, f.name),
          }))}
          selectedId={faceKey}
          minTile={76}
          imgAspect="1 / 1"
          onPick={(key) => { const f = model.faces.find((x) => x.key === key); if (f) pickFace(f.name); }}
        />
      </div>
    </div>
  );
}

// Collapsed-state swatch for a two-tone selection: base finish top-left, face finish bottom-right,
// split on the diagonal — same footprint as the Section header's plain colour swatch.
function SplitSwatch({ baseUrl, faceUrl, size = 28 }: { baseUrl: string | null; faceUrl: string | null; size?: number }) {
  const half = (url: string | null, clip: string) =>
    url ? (
      <img src={url} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", clipPath: clip }} />
    ) : (
      <span style={{ position: "absolute", inset: 0, background: "var(--bg)", clipPath: clip }} />
    );
  return (
    <span
      data-testid="split-swatch"
      style={{ position: "relative", width: size, height: size, borderRadius: "var(--radius-sm)", overflow: "hidden", border: "1px solid var(--border-strong)", display: "block", flexShrink: 0 }}
    >
      {half(baseUrl, "polygon(0 0, 100% 0, 0 100%)")}
      {half(faceUrl, "polygon(100% 0, 100% 100%, 0 100%)")}
    </span>
  );
}

function Tiles({ items, selectedId, minTile = 96, imgAspect = "4 / 3", cols, fit = "cover", onPick }: {
  // disabled = visible but not selectable (e.g. an overlay with no pre-combined row for the chosen base)
  // note = a small brand-colored line under the name (e.g. the insulation "+$X" upcharge)
  items: { id: string; name: string; imageUrl: string | null; disabled?: boolean; note?: string }[];
  selectedId: string | null;
  minTile?: number;
  imgAspect?: string;
  cols?: number; // fixed column count (Windows = 1, Panels = 2); omitted ⇒ default auto-fill grid
  fit?: "cover" | "contain"; // "contain" shows the whole option art uncropped, framed on --bg
  onPick: (id: string) => void;
}) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: cols ? `repeat(${cols}, 1fr)` : `repeat(auto-fill, minmax(${minTile}px, 1fr))`, gap: 8 }}>
      {items.map((it) => {
        const sel = it.id === selectedId;
        const off = !!it.disabled;
        return (
          <button
            key={it.id || "none"}
            type="button"
            className="rg-tile"
            disabled={off}
            onClick={() => { if (!off) onPick(it.id); }}
            style={{
              background: sel ? "var(--brand-tint)" : "var(--surface)",
              border: `${sel ? "1.5px" : "1px"} solid ${sel ? "var(--brand)" : "var(--border)"}`,
              boxShadow: sel ? "0 0 0 3px var(--ring)" : "none",
              borderRadius: "var(--radius-sm)",
              padding: 6,
              cursor: off ? "not-allowed" : "pointer",
              opacity: off ? 0.4 : 1,
              textAlign: "center",
            }}
          >
            {it.id === "" ? (
              // The synthetic "None" option (no window / no hardware) — an explicit choice, not a gap.
              <div style={{ width: "100%", aspectRatio: imgAspect, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--muted-2)", fontSize: 12, borderRadius: 6, background: "var(--bg)" }}>None</div>
            ) : (
              // Graceful thumb: a missing/404 option image shows a neutral placeholder, not a broken glyph.
              <Thumb src={it.imageUrl} aspect={imgAspect} fit={fit} />
            )}
            <div style={{ fontSize: 12, marginTop: 6, color: "var(--text)", fontWeight: sel ? 600 : 400 }}>{it.name}</div>
            {it.note && (
              <div style={{ fontSize: 11, marginTop: 2, color: "var(--brand-active)", fontWeight: 600 }}>{it.note}</div>
            )}
          </button>
        );
      })}
    </div>
  );
}
